// Domain constants: NS DNRR attendance codes, OT cost centres, expense types.
// Pure data — no imports — so calc.js and the UI can both use it.

export const ATTENDANCE_CODES = [
  { value: "0801", label: "0801 — Overtime",            type: "ot" },
  { value: "0804", label: "0804 — Standby",             type: "ot" },
  { value: "0200", label: "0200 — Sick Day",            type: "absence" },
  { value: "0216", label: "0216 — Time in Lieu (Banked OT)", type: "absence" },
];
// Full NS DNRR code key (template.xlsx legend) minus the 4 quick-picks above.
// Surfaced via the "Other codes" dropdown. type drives hour rollups (ot = worked
// time that counts hours; absence = time off that doesn't).
export const OTHER_CODES = [
  { value: "0800", label: "0800 — Regular Hours",                 type: "ot" },
  { value: "0803", label: "0803 — Call Back",                     type: "ot" },
  { value: "0825", label: "0825 — Holiday Worked",                type: "ot" },
  { value: "0820", label: "0820 — Training/Course",               type: "ot" },
  { value: "0826", label: "0826 — Holiday Not Worked",            type: "absence" },
  { value: "0100", label: "0100 — Vacation",                      type: "absence" },
  { value: "0101", label: "0101 — Accumulative Vacation",         type: "absence" },
  { value: "0201", label: "0201 — Short Term Illness",            type: "absence" },
  { value: "0203", label: "0203 — Medical Appointment",           type: "absence" },
  { value: "0209", label: "0209 — Ease Back Leave",               type: "absence" },
  { value: "0220", label: "0220 — Time off for Stat Holiday",     type: "absence" },
  { value: "0230", label: "0230 — Time in Lieu (Call Back)",      type: "absence" },
  { value: "0260", label: "0260 — WCB Paid",                      type: "absence" },
  { value: "0610", label: "0610 — Family Illness",                type: "absence" },
  { value: "0620", label: "0620 — Jury Duty",                     type: "absence" },
  { value: "0630", label: "0630 — Bereavement Leave",             type: "absence" },
  { value: "0631", label: "0631 — Emergency",                     type: "absence" },
  { value: "0640", label: "0640 — Storm/Hazard Paid",             type: "absence" },
  { value: "0641", label: "0641 — Storm/Hazard Unpaid",           type: "absence" },
];
export const ALL_CODES = [...ATTENDANCE_CODES, ...OTHER_CODES];
export const OT_CODES = [
  { value: "62", label: "62 — Resources",    costCentre: "160062" },
  { value: "60", label: "60 — Fire Control", costCentre: "160060" },
];
export const COMP_WAGE_TYPES = [
  { value: "0", label: "0 — Paid" },
  { value: "1", label: "1 — Banked" },
];

// ── Time-off balance accounts (mirrors ESS "Time Accounts") ──
// deducts: attendance codes whose entry hours subtract from the account.
// 0101 (Accumulative Vacation) and 0230 (TIL Call Back) are intentionally
// unmapped — add a code to a deducts list to start counting it.
export const TIME_ACCOUNTS = [
  { key: "vacation", label: "Vacation",             deducts: ["0100"] },
  { key: "sick",     label: "General Illness/Sick", deducts: ["0200", "0201"] },
  { key: "medical",  label: "Medical/Dental",       deducts: ["0203"] },
  { key: "family",   label: "Family Illness",       deducts: ["0610"] },
  { key: "banked",   label: "Banked time (TIL)",    deducts: ["0216"] },
];
// Banked accrual per OT hour when an entry is saved as Banked. Standby banks
// exactly one third (never 0.33). The 2× second-day-of-rest/stat override
// lives on the entry itself (doubleOt flag).
export const BANKED_RATE_DEFAULT = 1.5;
export const BANKED_RATE_STANDBY = 1 / 3;
export const BANKED_RATE_DOUBLE  = 2;

// Short type labels for the agenda cards
export const SHORT_CODE = {
  "0801": "Overtime", "0804": "Standby", "0200": "Sick Day", "0203": "Medical", "0216": "Banked OT",
  ...Object.fromEntries(OTHER_CODES.map(c => [c.value, (c.label.split("— ")[1] || c.label).trim()])),
};

export const TIME_OPTIONS = [];
for (let t = 5; t <= 28.5; t += 0.5) {
  const dh = t >= 24 ? Math.floor(t - 24) : Math.floor(t);
  const dm = t % 1 === 0.5 ? "30" : "00";
  TIME_OPTIONS.push({ value: t, label: `${dh}:${dm}` });
}

export const BLANK = { attendanceCode: "0801", compWageType: "0", otCode: "62", orderProject: "", reason: "", beforeStart: "", beforeEnd: "", afterStart: "", afterEnd: "", duringStart: "", duringEnd: "", doubleOt: false };

// ── Expenses (separate from OT: money, monthly, receipt photo) ──
// Meal per diems (breakfast/lunch/dinner) are the most common claim. They get
// their own quick-select chips at the top of the form; everything else is a
// regular single-type expense. The legacy `meal` type is kept only so any
// previously-logged generic-meal entries still render.
export const EXP_TYPES = {
  // Per-diem quick chips (no receipt needed)
  breakfast:     { label: "Breakfast",                      essLabel: "Breakfast Per Diem",              icon: "meal", perDiem: true },
  lunch:         { label: "Lunch",                          essLabel: "Lunch Per Diem",                  icon: "meal", perDiem: true },
  dinner:        { label: "Dinner",                         essLabel: "Dinner Per Diem",                 icon: "meal", perDiem: true },
  fullday:       { label: "Full Day Per Diem",              essLabel: "Full Day Meal Per Diem",          icon: "meal", perDiem: true },
  // Legacy — kept so old records still render
  meal:          { label: "Meals / per diem",               essLabel: "Misc Travel Expense",             icon: "meal" },
  // Dropdown types (all SAP expense types in alphabetical order)
  accom:         { label: "Accommodations",                 essLabel: "Accommodations",                  icon: "bed" },
  airfare:       { label: "Airfare",                        essLabel: "Airfare",                         icon: "car" },
  baggage:       { label: "Baggage Fee",                    essLabel: "Baggage Fee",                     icon: "box" },
  bfast_rcpt:    { label: "Breakfast (receipt)",            essLabel: "Breakfast",                       icon: "meal" },
  bus_nr:        { label: "Bus (no receipt)",               essLabel: "Bus (no receipt)",                icon: "car" },
  bus_r:         { label: "Bus (receipt)",                  essLabel: "Bus (receipt)",                   icon: "car" },
  callback:      { label: "Call Back",                      essLabel: "Call Back",                       icon: "tag" },
  car_rental:    { label: "Car Rental",                     essLabel: "Car Rental",                      icon: "car" },
  car_fuel:      { label: "Car Rental Fuel",                essLabel: "Car Rental Fuel",                 icon: "car" },
  conference:    { label: "Conference / Registration Fee",  essLabel: "Conference / Registration Fee",   icon: "box" },
  dinner_rcpt:   { label: "Dinner (receipt)",               essLabel: "Dinner",                          icon: "meal" },
  ferry:         { label: "Ferry",                          essLabel: "Ferry",                           icon: "car" },
  incidental:    { label: "Incidental Expense",             essLabel: "Incidental Expense",              icon: "tag" },
  lunch_rcpt:    { label: "Lunch (receipt)",                essLabel: "Lunch",                           icon: "meal" },
  misc:          { label: "Misc Travel Expense",            essLabel: "Misc Travel Expense",             icon: "tag" },
  park_nr:       { label: "Parking (no receipt)",           essLabel: "Parking (no receipt)",            icon: "car" },
  park_r:        { label: "Parking (receipt)",              essLabel: "Parking (receipt)",               icon: "car" },
  priv_accom:    { label: "Private Accommodation",          essLabel: "Private Accommodation",           icon: "bed" },
  taxi:          { label: "Taxi",                           essLabel: "Taxi",                            icon: "car" },
  toll_nr:       { label: "Toll (no receipt)",              essLabel: "Toll (no receipt)",               icon: "car" },
  toll_r:        { label: "Toll (receipt)",                 essLabel: "Toll (receipt)",                  icon: "car" },
  train:         { label: "Train",                          essLabel: "Train",                           icon: "car" },
  training:      { label: "Training",                       essLabel: "Training",                        icon: "box" },
  transport_mid: { label: "Transportation Midnight-6AM",    essLabel: "Transportation Midnight-6:00AM",  icon: "car" },
  travel:        { label: "Travel / mileage",               essLabel: "Travel / mileage",                icon: "car", isMileage: true },
  supply:        { label: "Supplies",                       essLabel: "Supplies",                        icon: "box" },
  other:         { label: "Other",                          essLabel: "Other",                           icon: "tag" },
};
export const PER_DIEM_KEYS = ["breakfast", "lunch", "dinner"];
// All SAP dropdown types in alphabetical order (mirrors the ESS dropdown).
export const OTHER_EXP_KEYS = [
  "accom", "airfare", "baggage", "bfast_rcpt", "bus_nr", "bus_r",
  "callback", "car_rental", "car_fuel", "conference",
  "dinner_rcpt", "ferry", "incidental", "lunch_rcpt", "misc",
  "park_nr", "park_r", "priv_accom", "taxi",
  "toll_nr", "toll_r", "train", "training", "transport_mid", "travel",
  "supply", "other",
];
export const PER_DIEM_RATES = { breakfast: 10, lunch: 17, dinner: 25 };
export const FULL_DAY_RATE  = 52;

export const BLANK_EXP = { type: "", amount: "", project: "", reason: "", location: "", receipt: "", date: "", meals: {}, fulldayAmt: "", costCentre: "62" };
